import React from 'react';
import { RotateCcw, XCircle, Download, X, StickyNote } from 'lucide-react';

function OrderDetailsModal({
  order,
  costBreakdown,
  costBreakdownLoading,
  costForm,
  onCostFormChange,
  onSaveCostComponents,
  parseOrderPayload,
  getOrderStatusLabel,
  getStageColors,
  rawMaterialUnitByName,
  onClose,
  onOpenNotes,
  onReplaceOrder,
  onCancelOrder,
  onDownloadCsv
}) {
  if (!order) return null;

  const payload = parseOrderPayload(order.payload) || {};
  const products = Array.isArray(payload.products) ? payload.products : [];
  const holdBreakdown = Array.isArray(order?.hold_breakdown) ? order.hold_breakdown : [];
  const finishedHoldBreakdown = Array.isArray(order?.finished_hold_breakdown) ? order.finished_hold_breakdown : [];
  const linkedPurchaseBreakdown = Array.isArray(order?.linked_purchase_breakdown) ? order.linked_purchase_breakdown : [];
  const statusLabel = getOrderStatusLabel(order);
  const palette = getStageColors(statusLabel);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal order-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="modal-close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <h3 className="order-modal-title">Order #{order.order_number}</h3>
        <div className="order-summary-card">
          <div className="order-summary-row">
            <span>Buyer</span>
            <strong>{order.buyer_name || '-'}</strong>
          </div>
          <div className="order-summary-row">
            <span>Products</span>
            <strong>{Number(order.product_count || products.length || 0)}</strong>
          </div>
          <div className="order-summary-row">
            <span>Total qty</span>
            <strong>{Number(order.total_qty || 0)}</strong>
          </div>
          <div className="order-summary-row">
            <span>Total required</span>
            <strong>{Number(order.total_required || 0).toFixed(2)} kg</strong>
          </div>
          <div className="order-summary-row">
            <span>Status</span>
            <span
              className="order-stage-chip"
              style={{ background: palette.bg, color: palette.fg }}
            >
              {statusLabel}
            </span>
          </div>
          <div className="order-summary-row order-summary-notes">
            <span>Notes</span>
            <div className="order-notes-inline">
              <p>{order.notes || 'No notes yet.'}</p>
              <button type="button" className="btn-ghost order-notes-btn" onClick={() => onOpenNotes(order)}>
                <StickyNote className="btn-icon" size={14} />
                <span>{order.notes ? 'Edit Notes' : 'Add Notes'}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="order-summary-card">
          <div className="order-summary-row">
            <span>Raw Material Cost</span>
            <strong>{Number(costBreakdown?.raw_material_cost_total || 0).toFixed(2)}</strong>
          </div>
          <div className="order-summary-row">
            <span>Finished Goods Cost</span>
            <strong>{Number(costBreakdown?.finished_goods_cost_total || 0).toFixed(2)}</strong>
          </div>
          <div className="order-summary-row">
            <span>Landed Cost</span>
            <strong>{Number(costBreakdown?.landed_cost_total || 0).toFixed(2)}</strong>
          </div>
          <div className="order-summary-row">
            <span>Cost Status</span>
            <strong>
              {costBreakdown?.completeness_flags?.has_fallback_costs ? 'Estimated (fallback present)' : 'Complete estimate'}
            </strong>
          </div>
          <div className="form-grid" style={{ marginTop: '8px' }}>
            <div className="form-group">
              <label>Labor</label>
              <input type="number" min="0" step="0.01" value={costForm.labor_cost} onChange={(e) => onCostFormChange('labor_cost', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Overhead</label>
              <input type="number" min="0" step="0.01" value={costForm.overhead_cost} onChange={(e) => onCostFormChange('overhead_cost', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Freight</label>
              <input type="number" min="0" step="0.01" value={costForm.freight_cost} onChange={(e) => onCostFormChange('freight_cost', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Misc</label>
              <input type="number" min="0" step="0.01" value={costForm.misc_cost} onChange={(e) => onCostFormChange('misc_cost', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Tax</label>
              <input type="number" min="0" step="0.01" value={costForm.tax_amount} onChange={(e) => onCostFormChange('tax_amount', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Discount</label>
              <input type="number" min="0" step="0.01" value={costForm.discount_amount} onChange={(e) => onCostFormChange('discount_amount', e.target.value)} />
            </div>
          </div>
          <div className="modal-actions" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn-primary" onClick={onSaveCostComponents} disabled={costBreakdownLoading}>
              {costBreakdownLoading ? 'Saving...' : 'Save Cost Inputs'}
            </button>
          </div>
        </div>

        <div className="order-items-header">
          <span className="order-items-pill">Items</span>
          <span className="order-items-count">{products.length} product{products.length === 1 ? '' : 's'}</span>
        </div>

        <div className="order-items-header">
          <span className="order-items-pill">Hold Breakdown</span>
          <span className="order-items-count">{holdBreakdown.length} category{holdBreakdown.length === 1 ? '' : 'ies'}</span>
        </div>
        {holdBreakdown.length === 0 ? (
          <div className="order-item-card empty">No held raw materials for this order.</div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Raw Material</th>
                  <th>Reserved</th>
                  <th>Consumed</th>
                  <th>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {holdBreakdown.map((item) => {
                  const materialName = String(item?.raw_material_name || '').trim();
                  const unit = rawMaterialUnitByName?.[materialName] || '';
                  return (
                    <tr key={`hold-breakdown-${materialName}`}>
                      <td>{materialName || '-'}</td>
                      <td>{Number(item?.reserved || 0).toFixed(2)}{unit ? ` ${unit}` : ''}</td>
                      <td>{Number(item?.consumed || 0).toFixed(2)}{unit ? ` ${unit}` : ''}</td>
                      <td>{Number(item?.remaining || 0).toFixed(2)}{unit ? ` ${unit}` : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="order-items-header">
          <span className="order-items-pill">Finished Allocation</span>
          <span className="order-items-count">{finishedHoldBreakdown.length} product{finishedHoldBreakdown.length === 1 ? '' : 's'}</span>
        </div>
        {finishedHoldBreakdown.length === 0 ? (
          <div className="order-item-card empty">No finished stock allocated for this order.</div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Reserved</th>
                  <th>Consumed</th>
                  <th>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {finishedHoldBreakdown.map((item, index) => (
                  <tr key={`fg-hold-breakdown-${item?.product_id || index}`}>
                    <td>{item?.product_name || 'Product'}</td>
                    <td>{Number(item?.reserved || 0).toFixed(2)}</td>
                    <td>{Number(item?.consumed || 0).toFixed(2)}</td>
                    <td>{Number(item?.remaining || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="order-items-header">
          <span className="order-items-pill">Linked Purchases</span>
          <span className="order-items-count">{linkedPurchaseBreakdown.length} bucket{linkedPurchaseBreakdown.length === 1 ? '' : 's'}</span>
        </div>
        {linkedPurchaseBreakdown.length === 0 ? (
          <div className="order-item-card empty">No linked purchases for this order yet.</div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Raw Material</th>
                  <th>Characteristics</th>
                  <th>Purchased</th>
                  <th>Consumed</th>
                  <th>Remaining</th>
                  <th>Remaining Cost</th>
                </tr>
              </thead>
              <tbody>
                {linkedPurchaseBreakdown.map((item, index) => {
                  const characteristicText = Object.entries(item?.characteristics || {})
                    .map(([key, value]) => `${key}: ${value}`)
                    .join(' • ');
                  return (
                    <tr key={`linked-purchase-${item?.raw_material_name || 'rm'}-${index}`}>
                      <td>{item?.raw_material_name || '-'}</td>
                      <td>{characteristicText || '-'}</td>
                      <td>{Number(item?.purchased || 0).toFixed(2)}</td>
                      <td>{Number(item?.consumed || 0).toFixed(2)}</td>
                      <td>{Number(item?.remaining || 0).toFixed(2)}</td>
                      <td>{Number(item?.remaining_cost || 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="order-items-list">
          {products.length === 0 ? (
            <div className="order-item-card empty">No products on this order.</div>
          ) : (
            products.map((product, index) => {
              const items = Array.isArray(product.items) ? product.items : [];
              return (
                <div key={`${product.product_id || product.product_name || 'product'}-${index}`} className="order-item-card">
                  <div className="order-item-header">
                    <div>
                      <strong>{product.product_name || 'Unnamed Product'}</strong>
                    </div>
                    <span className="order-item-chip">Qty {product.quantity || 0}</span>
                  </div>
                  {items.length === 0 ? (
                    <div className="order-item-empty">No raw materials listed.</div>
                  ) : (
                    items.map((item, idx) => {
                      const rawType = item.raw_material_name || item.raw_material_type || item.type || 'Raw material';
                      const characteristicText = Object.entries(item.characteristics || {})
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(' • ');
                      const perUnit = Number(item.required_qty || item.required_kg || 0);
                      const qty = Number(product.quantity || 0);
                      const totalReq = perUnit * (Number.isFinite(qty) ? qty : 0);
                      return (
                        <div key={`${rawType}-${idx}`} className="order-item-row">
                          <div>
                            <div className="order-item-title">{rawType}</div>
                            <div className="order-item-sub">{characteristicText || 'No characteristic selected'}</div>
                          </div>
                          <div className="order-item-need">
                            Need {Number.isFinite(totalReq) ? totalReq.toFixed(2) : '0.00'} kg
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="order-modal-actions">
          <div className="order-actions-left">
            <button type="button" className="btn-primary" onClick={() => onReplaceOrder(order)}>
              <RotateCcw className="btn-icon" size={16} />
              <span>Re-place Order</span>
            </button>
            {String(statusLabel).toLowerCase() !== 'completed' && String(statusLabel).toLowerCase() !== 'cancelled' && (
              <button
                type="button"
                className="btn-danger"
                onClick={() => onCancelOrder(order)}
              >
                <XCircle className="btn-icon" size={16} />
                <span>Cancel Order</span>
              </button>
            )}
          </div>
          <div className="order-actions-right">
            <button type="button" className="btn-ghost" onClick={() => onDownloadCsv(order)}>
              <Download className="btn-icon" size={16} />
              <span>Download CSV (Excel)</span>
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              <X className="btn-icon" size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetailsModal;
